import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import { Partner, PartnersService } from '../../../services/partners/partners.service';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../../brokeragency/member-addedit/member-addedit.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';

type PrimaryInsuranceCustomFieldType = 'string' | 'number' | 'boolean' | 'date' | 'dropdown';

interface PrimaryInsuranceCustomField {
  fieldName: string;
  label: string;
  type: PrimaryInsuranceCustomFieldType;
  options?: string[];
}

interface PrimaryInsuranceConfig {
  hidden?: boolean;
  customFields?: PrimaryInsuranceCustomField[];
}

interface InviteEmailAppConfig {
  downloadUrl?: string;
  qrCodeUrl?: string;
}

interface InviteEmailConfig {
  mode?: 'web' | 'app';
  app?: InviteEmailAppConfig;
}

const PRIMARY_INSURANCE_CUSTOM_FIELD_TYPES: PrimaryInsuranceCustomFieldType[] = ['string', 'number', 'boolean', 'date', 'dropdown'];


@Component({
    selector: 'app-brands-page',
    templateUrl: './brands-page.component.html',
    styleUrls: ['./brands-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BrandsPageComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  protected allColumnNames = ['name', 'colors', 'url', 'logo', 'actionmenu'];
  protected items = new MatTableDataSource<any>();

  private addEditFields = [
    {name: 'name', label: 'Brand name', fieldType: FieldType.F_STRING, required: true},
    {
      name: 'redirectUrl',
      label: 'URL',
      fieldType: FieldType.F_STRING,
      required: false,
      pattern: '^(?:[A-Za-z0-9-]+\\.)+[A-Za-z]{2,}$',
      placeholder: 'example.wallit.app',
      hint: 'Enter the full hostname without https://'
    },
    {name: 'ourlyPrimaryColor', label: 'ourlyPrimaryColor', fieldType: FieldType.F_COLOR, required: false},
    {name: 'ourlySecondaryColor', label: 'ourlySecondaryColor', fieldType: FieldType.F_COLOR, required: false},
    {name: 'wallitTertiaryColor', label: 'wallitTertiaryColor', fieldType: FieldType.F_COLOR, required: false},
    {name: 'wallitGradientNEColor', label: 'wallitGradientNEColor', fieldType: FieldType.F_COLOR, required: false},
    {name: 'wallitGradientSWColor', label: 'wallitGradientNEColor', fieldType: FieldType.F_COLOR, required: false},
    {name: 'logoUrl', label: 'Logo on Light', fieldType: FieldType.F_IMAGE, required: false},
    {name: 'logoUrlImageData', invisible: () => true, label: 'logoUrlImageData (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'logoUrlImageDataSrc', invisible: () => true, label: 'logoUrlImageDataSrc (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'lightLogoUrl', label: 'Logo on Dark', fieldType: FieldType.F_IMAGE, required: false},
    {name: 'lightLogoUrlImageData', invisible: () => true, label: 'lightLogoUrlImageData (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'lightLogoUrlImageDataSrc', invisible: () => true, label: 'lightLogoUrlImageData (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'horizontalLogoOnWhite', label: 'Horizontal Logo on White', fieldType: FieldType.F_IMAGE, required: false},
    {name: 'horizontalLogoOnWhiteImageData', invisible: () => true, label: 'horizontalLogoOnWhiteImageData (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'horizontalLogoOnWhiteImageDataSrc', invisible: () => true, label: 'horizontalLogoOnWhiteImageDataSrc (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'horizontalLogoUrl', label: 'Horizontal Logo on Dark', fieldType: FieldType.F_IMAGE, required: false},
    {name: 'horizontalLogoUrlImageData', invisible: () => true, label: 'horizontalLogoUrlImageData (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'horizontalLogoUrlImageDataSrc', invisible: () => true, label: 'horizontalLogoUrlImageDataSrc (invisible)', fieldType: FieldType.F_HIDDEN_DATA, required: false},
    {name: 'signupDisabled', label: 'Signup disabled', fieldType: FieldType.F_TOGGLE, required: false},
    {
      name: 'retainBrandAfterTeamRemoval',
      label: 'Keep brand after team/company removal',
      fieldType: FieldType.F_TOGGLE,
      required: false,
      hint: 'Keep this brand and brand-based support access after removal, including team deletion. Existing membership and benefit removal rules still apply.',
    },
    {
      name: 'inviteEmailMode',
      label: 'Invite Email Mode',
      fieldType: FieldType.F_DROPDOWN,
      values: ['Web:web', 'App:app'],
      required: false,
      hint: 'App mode uses the app-style CTA only when both the download URL and QR image are saved.',
    },
    {
      name: 'inviteEmailDownloadUrl',
      label: 'Invite App Download URL',
      fieldType: FieldType.F_STRING,
      required: false,
      invisible: (form: any) => form?.get('inviteEmailMode')?.value !== 'app',
      hint: 'Invites fall back to the current web email until both app fields are present.',
    },
    {
      name: 'inviteEmailQrCodeUrl',
      label: 'Invite App QR Image',
      fieldType: FieldType.F_IMAGE,
      required: false,
      invisible: (form: any) => form?.get('inviteEmailMode')?.value !== 'app',
    },
    {
      name: 'inviteEmailQrCodeUrlImageData',
      invisible: () => true,
      label: 'inviteEmailQrCodeUrlImageData (invisible)',
      fieldType: FieldType.F_HIDDEN_DATA,
      required: false,
    },
    {
      name: 'inviteEmailQrCodeUrlImageDataSrc',
      invisible: () => true,
      label: 'inviteEmailQrCodeUrlImageDataSrc (invisible)',
      fieldType: FieldType.F_HIDDEN_DATA,
      required: false,
    },
    {name: 'configjson', label: 'Config JSON', fieldType: FieldType.F_JSON, required: false, validateJson: (control: any) => this.validateJson(control)},
    {name: 'primaryInsuranceHidden', label: 'Hide Primary Insurance', fieldType: FieldType.F_TOGGLE, required: false},
    {
      name: 'primaryInsuranceCustomFields',
      label: 'Primary Insurance Custom Fields',
      fieldType: FieldType.F_PRIMARY_INSURANCE_CUSTOM_FIELDS,
      required: false,
      validateJson: (control: any) => this.validatePrimaryInsuranceCustomFields(control),
      invisible: (form: any) => !!form?.get('primaryInsuranceHidden')?.value,
      hint: 'Example: [{"fieldName":"eyeColor","label":"Eye Color","type":"dropdown","options":["Brown","Blue"]}]'
    },
  ];

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private partnersService: PartnersService,
    private spinnerService: SpinnerService,
    private uiAlertService: UiAlertService,
  ) {}

  ngOnInit(): void {
    this.reloadItems();
  }

  addItem(baseData?: Record<string, any>): void {
    // Set data to null if creating from scratch to get proper titles in MemberAddeditDialog
    let newBrandConfigData: Record<string, any> | null = this.buildDialogData(baseData, {forDuplicate: !!baseData});
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(newBrandConfigData, this.addEditFields, 'Brand Config')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        // If creating from scratch, initialize data to be an empty object
        if (!newBrandConfigData) {
          newBrandConfigData = {};
        }
        // Update values in item before PUTing to API
        for (const key of Object.keys(dialogResult)) {
          if (['primaryInsuranceHidden', 'primaryInsuranceCustomFields', 'inviteEmailMode', 'inviteEmailDownloadUrl', 'inviteEmailQrCodeUrl', 'retainBrandAfterTeamRemoval'].includes(key)) {
            continue;
          }
          newBrandConfigData[key] = dialogResult[key];
        }
        this.applyPrimaryInsuranceSettings(newBrandConfigData, dialogResult);
        this.syncUrlFields(newBrandConfigData);
        this.applyInviteEmailSettings(newBrandConfigData, dialogResult);
        this.applyBrandRetentionSettings(newBrandConfigData, dialogResult);
        this.partnersService.createPartner(newBrandConfigData as Partner).then(async (createdPartner) => {
          if (dialogResult.inviteEmailQrCodeUrlImageData && createdPartner?.partnerId) {
            await this.partnersService.uploadInviteQr(createdPartner.partnerId, dialogResult.inviteEmailQrCodeUrlImageData);
          }
          this.reloadItems();
          this.snackBar.open('Brand config created', 'Close', {
            duration: 3000
          });
        }).catch((error: HttpErrorResponse) => {
          const message = error?.error?.errors;
          this.snackBar.open(typeof message === 'string' ? message : 'Unable to create brand config', 'Close', {
            duration: 6000
          });
        });
      }
    });
  }

  clickMenuItem(item: any): void {
    console.log(item);
  }

  deleteItem(item: any): void {
    this.uiAlertService.presentAlertConfirm('Are you sure you want to delete this brand config?').then(async confirm => {
      if (confirm) {
        this.spinnerService.show('Deleting brand config...');
        this.partnersService.deletePartner(item).then(() => {
          this.reloadItems();
          this.spinnerService.hide();
          this.snackBar.open('Brand config deleted', 'Close', {
            duration: 3000
          });
        }).catch((err: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.snackBar.open('Unable to delete brand config', 'Close', {
            duration: 3000
          });
        });
      }
    });
  }

  editItem(item: any): void {
    const dialogItem = this.buildDialogData(item);
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(dialogItem, this.addEditFields, 'Brand Config')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        const draft = structuredClone(item);
        const uploadLogoTasks: Array<() => Promise<void>> = [];
        // Keep the displayed item unchanged until the saved data is reloaded.
        for (const key of Object.keys(dialogResult)) {
          if (['primaryInsuranceHidden', 'primaryInsuranceCustomFields', 'inviteEmailMode', 'inviteEmailDownloadUrl', 'inviteEmailQrCodeUrl', 'retainBrandAfterTeamRemoval'].includes(key)) {
            continue;
          }
          if (key.endsWith('ImageData')) {
            const baseKey = key.substring(0, key.indexOf('ImageData'));
            const logoType = this.logoTypeForBaseKey(baseKey);

            if (dialogResult[key] && logoType) {
              uploadLogoTasks.push(() => this.partnersService.changePartnerLogo(item.partnerId, dialogResult[key], logoType));
              draft[baseKey] = '';
            }
          }  else if (key.endsWith('ImageDataSrc')) {
            // Do nothing with the image src data, this is used to display on the edit component
          } else {
            draft[key] = dialogResult[key];
          }
        }
        this.applyPrimaryInsuranceSettings(draft, dialogResult);
        this.syncUrlFields(draft);
        this.applyInviteEmailSettings(draft, dialogResult);
        this.applyBrandRetentionSettings(draft, dialogResult);
        if (dialogResult.inviteEmailQrCodeUrlImageData) {
          uploadLogoTasks.push(() =>
            this.partnersService.uploadInviteQr(item.partnerId, dialogResult.inviteEmailQrCodeUrlImageData).then(() => undefined)
          );
        }
        this.partnersService.updatePartner(draft).then(async (_updatedPartner) => {
          // Save configjson first, then upload assets that patch individual URLs.
          await Promise.all(uploadLogoTasks.map(task => task()));
            this.reloadItems(item.partnerId);
            this.snackBar.open('Brand config updated', 'Close', {
              duration: 3000
            });
        }).catch((error: HttpErrorResponse) => {
          const message = error?.error?.errors;
          this.snackBar.open(typeof message === 'string' ? message : 'Unable to save brand config', 'Close', {
            duration: 6000
          });
        });
      }
    });
  }

  // TODO: update with full json validation for brands config
  validateJson(control: any): any {
    if (control.value) {
      for (const band of control.value?.bands || []) {
        if (!band?.title || !band?.type) {
          return { badBand: band };
        }
      }
      const primaryInsuranceError = this.getPrimaryInsuranceValidationError(control.value?.profile?.primaryInsurance);
      if (primaryInsuranceError) {
        return primaryInsuranceError;
      }
      const inviteEmailError = this.getInviteEmailValidationError(control.value?.inviteEmail);
      if (inviteEmailError) {
        return inviteEmailError;
      }
    }
    return null;
  }

  validatePrimaryInsuranceCustomFields(control: any): any {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null;
    }

    const primaryInsuranceError = this.getPrimaryInsuranceValidationError({
      hidden: false,
      customFields: control.value
    });
    if (primaryInsuranceError?.badPrimaryInsuranceCustomFields) {
      return primaryInsuranceError;
    }
    return null;
  }

  private logoTypeForBaseKey(baseKey: string): 'avatar' | 'lightlogo' | 'horizontallogo' | 'horizontallightlogo' | null {
    switch (baseKey) {
      case 'logoUrl':
        return 'avatar';
      case 'lightLogoUrl':
        return 'lightlogo';
      case 'horizontalLogoOnWhite':
        return 'horizontallightlogo';
      case 'horizontalLogoUrl':
        return 'horizontallogo';
      default:
        return null;
    }
  }

  private buildDialogData(
    baseData?: Record<string, any>,
    options?: { forDuplicate?: boolean }
  ): Record<string, any> | null {
    if (!baseData) {
      return null;
    }

    const plainData = typeof (baseData as any).plain === 'function'
      ? (baseData as any).plain()
      : baseData;
    const dialogData = structuredClone(plainData);
    const primaryInsuranceConfig = this.getPrimaryInsuranceConfig(dialogData.configjson);
    const inviteEmailConfig = this.getInviteEmailConfig(dialogData.configjson);

    dialogData.primaryInsuranceHidden = !!primaryInsuranceConfig.hidden;
    dialogData.primaryInsuranceCustomFields = primaryInsuranceConfig.customFields || [];
    dialogData.redirectUrl = this.normalizeRedirectUrl(dialogData.redirectUrl);
    dialogData.inviteEmailMode = inviteEmailConfig.mode || 'web';
    dialogData.inviteEmailDownloadUrl = inviteEmailConfig.app?.downloadUrl || '';
    dialogData.inviteEmailQrCodeUrl = inviteEmailConfig.app?.qrCodeUrl || '';
    dialogData.retainBrandAfterTeamRemoval = dialogData.configjson?.retainBrandAfterTeamRemoval === true;

    if (options?.forDuplicate) {
      delete dialogData.partnerId;
      dialogData.name = dialogData.name ? `${dialogData.name} - COPY` : dialogData.name;
      dialogData.redirectUrl = '';
      dialogData.domain = '';
      dialogData.shortName = '';
    }

    return dialogData;
  }

  private syncUrlFields(target: Record<string, any>): void {
    const redirectUrl = this.normalizeRedirectUrl(target.redirectUrl);
    target.redirectUrl = redirectUrl;

    if (!redirectUrl) {
      target.domain = '';
      target.shortName = '';
      return;
    }

    target.domain = redirectUrl;
    target.shortName = this.getShortNameFromRedirectUrl(redirectUrl);
  }

  private normalizeRedirectUrl(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/+$/, '');
  }

  private getShortNameFromRedirectUrl(redirectUrl: string): string {
    const parts = redirectUrl.split('.').filter(Boolean);
    if (parts.length <= 2) {
      return parts[0] || '';
    }
    return parts.slice(0, -2).join('.');
  }

  private applyPrimaryInsuranceSettings(target: Record<string, any>, dialogResult: Record<string, any>): void {
    target.configjson = target.configjson && typeof target.configjson === 'object' ? target.configjson : {};
    target.configjson.profile = target.configjson.profile && typeof target.configjson.profile === 'object'
      ? target.configjson.profile
      : {};

    const primaryInsuranceConfig: PrimaryInsuranceConfig = {
      hidden: !!dialogResult.primaryInsuranceHidden,
      customFields: this.normalizePrimaryInsuranceCustomFields(dialogResult.primaryInsuranceCustomFields)
    };

    target.configjson.profile.primaryInsurance = primaryInsuranceConfig;
    delete target.primaryInsuranceHidden;
    delete target.primaryInsuranceCustomFields;
  }

  private applyBrandRetentionSettings(target: Record<string, any>, dialogResult: Record<string, any>): void {
    target.configjson = target.configjson && typeof target.configjson === 'object' ? target.configjson : {};
    target.configjson.retainBrandAfterTeamRemoval = dialogResult.retainBrandAfterTeamRemoval === true;
    delete target.retainBrandAfterTeamRemoval;
  }

  private applyInviteEmailSettings(target: Record<string, any>, dialogResult: Record<string, any>): void {
    target.configjson = target.configjson && typeof target.configjson === 'object' ? target.configjson : {};

    const inviteEmailMode = dialogResult.inviteEmailMode === 'app' ? 'app' : 'web';
    const downloadUrl = `${dialogResult.inviteEmailDownloadUrl || ''}`.trim();
    const qrCodeUrl = `${dialogResult.inviteEmailQrCodeUrl || ''}`.trim();

    target.configjson.inviteEmail = {
      mode: inviteEmailMode,
      app: {
        ...(downloadUrl ? {downloadUrl} : {}),
        ...(qrCodeUrl ? {qrCodeUrl} : {}),
      },
    };

    delete target.inviteEmailMode;
    delete target.inviteEmailDownloadUrl;
    delete target.inviteEmailQrCodeUrl;
  }

  private getPrimaryInsuranceConfig(configjson: any): PrimaryInsuranceConfig {
    const primaryInsurance = configjson?.profile?.primaryInsurance;
    return {
      hidden: !!primaryInsurance?.hidden,
      customFields: this.normalizePrimaryInsuranceCustomFields(primaryInsurance?.customFields)
    };
  }

  private getInviteEmailConfig(configjson: any): InviteEmailConfig {
    const inviteEmail = configjson?.inviteEmail;
    return {
      mode: inviteEmail?.mode === 'app' ? 'app' : 'web',
      app: {
        ...(typeof inviteEmail?.app?.downloadUrl === 'string' && inviteEmail.app.downloadUrl.trim()
          ? {downloadUrl: inviteEmail.app.downloadUrl.trim()}
          : {}),
        ...(typeof inviteEmail?.app?.qrCodeUrl === 'string' && inviteEmail.app.qrCodeUrl.trim()
          ? {qrCodeUrl: inviteEmail.app.qrCodeUrl.trim()}
          : {}),
      },
    };
  }

  private getPrimaryInsuranceValidationError(primaryInsurance: any): any {
    if (primaryInsurance === undefined || primaryInsurance === null) {
      return null;
    }

    if (typeof primaryInsurance !== 'object' || Array.isArray(primaryInsurance)) {
      return { badPrimaryInsuranceConfig: primaryInsurance };
    }

    if (primaryInsurance.hidden !== undefined && typeof primaryInsurance.hidden !== 'boolean') {
      return { badPrimaryInsuranceHidden: primaryInsurance.hidden };
    }

    if (primaryInsurance.customFields !== undefined && !Array.isArray(primaryInsurance.customFields)) {
      return { badPrimaryInsuranceCustomFields: primaryInsurance.customFields };
    }

    for (const customField of primaryInsurance.customFields || []) {
      if (!this.isValidPrimaryInsuranceCustomField(customField)) {
        return { badPrimaryInsuranceCustomFields: customField };
      }
    }

    return null;
  }

  private getInviteEmailValidationError(inviteEmail: any): any {
    if (inviteEmail === undefined || inviteEmail === null) {
      return null;
    }

    if (typeof inviteEmail !== 'object' || Array.isArray(inviteEmail)) {
      return { badInviteEmailConfig: inviteEmail };
    }

    if (inviteEmail.mode !== undefined && !['web', 'app'].includes(inviteEmail.mode)) {
      return { badInviteEmailMode: inviteEmail.mode };
    }

    if (inviteEmail.app !== undefined && (typeof inviteEmail.app !== 'object' || Array.isArray(inviteEmail.app))) {
      return { badInviteEmailApp: inviteEmail.app };
    }

    if (
      inviteEmail?.app?.downloadUrl !== undefined
      && typeof inviteEmail.app.downloadUrl !== 'string'
    ) {
      return { badInviteEmailDownloadUrl: inviteEmail.app.downloadUrl };
    }

    if (
      inviteEmail?.app?.qrCodeUrl !== undefined
      && typeof inviteEmail.app.qrCodeUrl !== 'string'
    ) {
      return { badInviteEmailQrCodeUrl: inviteEmail.app.qrCodeUrl };
    }

    return null;
  }

  private isValidPrimaryInsuranceCustomField(customField: any): customField is PrimaryInsuranceCustomField {
    if (!customField || typeof customField !== 'object' || Array.isArray(customField)) {
      return false;
    }

    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(customField.fieldName || '')) {
      return false;
    }

    if (typeof customField.label !== 'string' || !customField.label.trim()) {
      return false;
    }

    if (!PRIMARY_INSURANCE_CUSTOM_FIELD_TYPES.includes(customField.type)) {
      return false;
    }

    if (customField.type === 'dropdown') {
      if (!Array.isArray(customField.options) || customField.options.length === 0) {
        return false;
      }
      if (customField.options.some((option: any) => typeof option !== 'string' || !option.trim())) {
        return false;
      }
    }

    return true;
  }

  private normalizePrimaryInsuranceCustomFields(customFields: any): PrimaryInsuranceCustomField[] {
    if (!Array.isArray(customFields)) {
      return [];
    }

    return customFields
      .filter(customField => this.isValidPrimaryInsuranceCustomField(customField))
      .map((customField: PrimaryInsuranceCustomField) => ({
        fieldName: customField.fieldName,
        label: customField.label.trim(),
        type: customField.type,
        ...(customField.type === 'dropdown' ? {options: (customField.options || []).map(option => option.trim())} : {})
      }));
  }

  private reloadItems(reloadAvatarImageForPartnerId?: string): void {
    this.partnersService.getAllPartners().then(partners => {
      if (partners && partners.length > 0 && reloadAvatarImageForPartnerId) {
        const timestamp = new Date().getTime();
        const partnerToReloadAvatarImageFor = partners.find(partner => partner.partnerId === reloadAvatarImageForPartnerId);
        if (partnerToReloadAvatarImageFor) {
          partnerToReloadAvatarImageFor.logoUrl = partnerToReloadAvatarImageFor?.logoUrl
            ? partnerToReloadAvatarImageFor.logoUrl += `?reloadTimestamp=${timestamp}`
            : '';
        }
      }
      // Default to alphabetical sort
      this.items.data = partners.sort((partner1, partner2) => partner1.name > partner2.name ? 1 : -1);
      this.items.sort = this.sort;
    });
  }
}
